import type { ReactNode } from 'react';

import { memo, useState } from 'react';

import useIdleCallback from '@core/hooks/useIdleCallback';

export interface DeferLoaderProps {
  /** Content to render once the browser is idle. */
  children: ReactNode;

  /** When true, renders children immediately. */
  disabled?: boolean;

  /** Placeholder to render until idle. */
  fallback?: ReactNode;

  /** Max wait time (ms) before rendering children. */
  timeout?: number;
}

/**
 * Defer rendering children until the browser is idle.
 */
const DeferLoader = memo(function DeferLoader({
  children,
  fallback = null,
  timeout,
  disabled = false,
}: DeferLoaderProps) {
  const [shouldLoad, setShouldLoad] = useState(disabled);

  // Schedule idle callback unless disabled or already loaded.
  useIdleCallback(() => setShouldLoad(true), { timeout, skip: disabled || shouldLoad });

  return <>{shouldLoad ? children : fallback}</>;
});

export default DeferLoader;
